import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

/**
 * Minimal top-level error boundary.
 *
 * React unmounts the entire component tree when a render throws and no
 * boundary catches it — on this dark-themed app that surfaces as a completely
 * blank/black screen with no hint of what went wrong. This boundary contains
 * the failure to a readable fallback (with a reload affordance) and logs the
 * real error to the console so the underlying cause stays diagnosable.
 *
 * Intentionally tiny and dependency-free: it must never be the thing that
 * throws. No product chrome, no data fetching, no context reads.
 */

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface the real error + component stack for debugging (DevTools console
    // and any error-forwarding the host injects). Never swallow it silently.
    console.error('[Rager] Erro de renderização não tratado:', error, info)
  }

  private handleReload = (): void => {
    if (typeof window !== 'undefined') window.location.reload()
  }

  render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          padding: '24px',
          background: 'var(--ink, #07080d)',
          color: 'var(--text, #e7e7ee)',
          fontFamily: 'var(--sans, system-ui, sans-serif)',
        }}
      >
        <div style={{ maxWidth: 440, textAlign: 'center' }}>
          <h1 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600 }}>
            Algo deu errado
          </h1>
          <p
            style={{
              margin: '0 0 18px',
              fontSize: 13.5,
              lineHeight: 1.55,
              color: 'var(--text-soft, #a9a9b8)',
            }}
          >
            A interface encontrou um erro inesperado e não pôde continuar.
            Recarregar a página costuma resolver. Se o problema persistir,
            contate o suporte.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: '9px 16px',
              fontSize: 13,
              fontWeight: 500,
              color: '#fff',
              borderRadius: 10,
              cursor: 'pointer',
              background:
                'linear-gradient(180deg, var(--violet, #7c5cff) 0%, var(--violet-deep, #5b3fd6) 100%)',
            }}
          >
            Recarregar
          </button>
          {error.message && (
            <pre
              style={{
                marginTop: 18,
                padding: '10px 12px',
                fontSize: 11.5,
                textAlign: 'left',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: 'var(--text-mute, #8a8a99)',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 8,
              }}
            >
              {error.message}
            </pre>
          )}
        </div>
      </div>
    )
  }
}
